/**
 * @file World: entity storage, cached queries and the system pipeline.
 *
 * Design notes:
 * - Entity objects are pooled and recycled to keep per-frame allocations near
 *   zero while bullets spawn and die.
 * - Destruction is deferred to the end of the update so a system can never
 *   iterate an array that another system just mutated.
 * - Queries are cached per bitmask and rebuilt lazily, only after a structural
 *   change (component added/removed, entity created/destroyed).
 */

import { Entity } from './Entity.js';
import { maskOf } from './Component.js';
import { Events } from '../core/EventBus.js';

/**
 * @typedef {object} Query
 * @property {number} mask Component bits required.
 * @property {Entity[]} entities Cached result, valid while `dirty` is false.
 * @property {boolean} dirty Whether the cache must be rebuilt on next read.
 */

export class World {
  /**
   * @param {import('../core/EventBus.js').EventBus} events Shared event bus.
   */
  constructor(events) {
    /** @type {import('../core/EventBus.js').EventBus} */
    this.events = events;
    /** @type {Entity[]} Dense list of live entities. */
    this.entities = [];
    /** @type {Map<number, Entity>} */
    this.byId = new Map();
    /** @type {Entity[]} Recycled entity objects. */
    this.pool = [];
    /** @type {Entity[]} Destroyed this frame, flushed by `flush()`. */
    this.pendingDestroy = [];
    /** @type {Map<number, Query>} */
    this.queries = new Map();
    /** @type {import('./System.js').System[]} */
    this.systems = [];
    /** @type {import('../core/Context.js').GameContext | null} */
    this.ctx = null;
    this.nextId = 1;
  }

  /**
   * Binds the shared game context injected into every system.
   * @param {import('../core/Context.js').GameContext} ctx Shared context.
   * @returns {void}
   */
  setContext(ctx) {
    this.ctx = ctx;
  }

  // ---------------------------------------------------------------- entities

  /**
   * Creates (or recycles) an entity.
   * @param {string} [name] Optional debug label.
   * @returns {Entity}
   */
  createEntity(name = '') {
    const id = this.nextId++;
    const entity = this.pool.pop() ?? new Entity(id, this);
    if (entity.id !== id || !entity.alive) entity._recycle(id);
    entity.name = name;

    this.entities.push(entity);
    this.byId.set(id, entity);
    this._invalidateQueries();
    this.events.emit(Events.ENTITY_CREATED, entity);
    return entity;
  }

  /**
   * Flags an entity for destruction at the end of the current update.
   * Safe to call several times for the same entity.
   * @param {Entity} entity Entity to destroy.
   * @returns {void}
   */
  destroyEntity(entity) {
    if (!entity || !entity.alive) return;
    entity.alive = false;
    this.pendingDestroy.push(entity);
  }

  /**
   * @param {number} id Entity id.
   * @returns {Entity | undefined}
   */
  getEntity(id) {
    return this.byId.get(id);
  }

  /**
   * Applies deferred destructions. Called automatically by {@link World#update}.
   * @returns {void}
   */
  flush() {
    if (this.pendingDestroy.length === 0) return;

    for (const entity of this.pendingDestroy) {
      // Listeners (render, audio, particles) release their resources here.
      this.events.emit(Events.ENTITY_DESTROYED, entity);
      this.byId.delete(entity.id);

      const index = this.entities.indexOf(entity);
      if (index !== -1) {
        // Swap-remove: order is irrelevant and this stays O(1).
        this.entities[index] = this.entities[this.entities.length - 1];
        this.entities.pop();
      }
      entity.components.clear();
      entity.mask = 0;
      this.pool.push(entity);
    }

    this.pendingDestroy.length = 0;
    this._invalidateQueries();
  }

  // ----------------------------------------------------------------- queries

  /**
   * Returns the live array of entities owning every given component type.
   * The array is cached and reused: do not mutate or retain it across frames.
   * @param {...Function} Types Component constructors.
   * @returns {Entity[]}
   */
  query(...Types) {
    return this.queryMask(maskOf(...Types));
  }

  /**
   * Query variant for a pre-computed mask, so systems can hoist `maskOf(...)`
   * out of the hot path.
   * @param {number} mask Combined component bits.
   * @returns {Entity[]}
   */
  queryMask(mask) {
    let query = this.queries.get(mask);
    if (!query) {
      query = { mask, entities: [], dirty: true };
      this.queries.set(mask, query);
    }
    if (query.dirty) {
      const result = query.entities;
      result.length = 0;
      for (const entity of this.entities) {
        if (entity.alive && (entity.mask & mask) === mask) result.push(entity);
      }
      query.dirty = false;
    }
    return query.entities;
  }

  /**
   * Invalidates cached queries when an entity gains or loses a component.
   * @param {Entity} _entity Changed entity.
   * @returns {void}
   * @internal
   */
  onEntityStructureChanged(_entity) {
    this._invalidateQueries();
  }

  /** @returns {void} @private */
  _invalidateQueries() {
    for (const query of this.queries.values()) query.dirty = true;
  }

  // ----------------------------------------------------------------- systems

  /**
   * Registers a system. Systems run in registration order unless they declare a
   * static `priority`, which is applied as a stable sort.
   * @template {import('./System.js').System} T
   * @param {T} system System instance.
   * @returns {T} The same system, for chaining.
   */
  addSystem(system) {
    system._attach(this, this.ctx);
    this.systems.push(system);
    this.systems.sort(
      (a, b) =>
        (/** @type {any} */ (a.constructor).priority ?? 0) -
        (/** @type {any} */ (b.constructor).priority ?? 0)
    );
    system.init();
    return system;
  }

  /**
   * @template {import('./System.js').System} T
   * @param {new (...args: any[]) => T} Type System constructor.
   * @returns {T | undefined}
   */
  getSystem(Type) {
    return /** @type {T | undefined} */ (this.systems.find((s) => s instanceof Type));
  }

  /**
   * Runs every enabled system, then flushes pending destructions.
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    for (const system of this.systems) {
      if (system.enabled) system.update(dt);
    }
    this.flush();
  }

  /**
   * Destroys every entity and disposes every system.
   * @returns {void}
   */
  clear() {
    for (const entity of [...this.entities]) this.destroyEntity(entity);
    this.flush();
  }

  /**
   * Full teardown: systems disposed, entities dropped, caches cleared.
   * @returns {void}
   */
  dispose() {
    for (const system of this.systems) system.dispose();
    this.systems.length = 0;
    this.clear();
    this.queries.clear();
    this.pool.length = 0;
  }
}
